import type { PageHelp } from '../../help/types';

/** Guide for `/settings/phrase-sync` — bringing Glossa up to date with Phrase (ADR 0008). */
export const phraseSyncHelp: PageHelp = {
  title: 'Phrase sync',
  intro:
    'Until every application has moved to Glossa, translators keep working in Phrase. A sync reads Phrase once, shows per project what would change, and writes nothing until you apply that preview. Only a superadmin sees this page.',
  sections: ['Running a sync', 'Conflicts', 'History'],
  contexts: {
    'start-dialog': 'Click “Start sync” at the top right.',
  },
  tips: [
    'A text changed only in Phrase is written into Glossa. A text changed only in Glossa stays. A text changed in both becomes a conflict, and Glossa’s text stays until you decide.',
    'Glossa compares every text with what Phrase had at the last sync, not with who saved it last — so texts copied into Glossa before the first sync are recognised as Phrase’s.',
    'A sync never deletes a key and never clears a text: a key or a text that disappeared from Phrase is only reported.',
    'Nothing is published. A project is stopped instead of synced if its change would reach a publish target that is not on this deployment’s allowed list, or any target with auto-publish.',
  ],
  steps: [
    // ── Running a sync ──────────────────────────────────────────────────────────
    {
      selector: '[data-help="phrase-sync-start"]',
      title: 'Start a sync',
      body: 'Opens a short dialog: every project, or only the ones you pick. The run then reads the whole Phrase dictionary once and previews the projects. This takes a while — Phrase limits how fast it answers — and the page updates by itself. Disabled while another run is open or when no Phrase token is configured.',
      section: 'Running a sync',
      i18n: {
        'de-AT': {
          title: 'Eine Synchronisierung starten',
          body: 'Öffnet einen kurzen Dialog: alle Projekte oder nur die ausgewählten. Der Lauf liest dann das gesamte Phrase-Wörterbuch einmal und erstellt die Vorschau der Projekte. Das dauert eine Weile — Phrase begrenzt, wie schnell es antwortet — und die Seite aktualisiert sich selbst. Gesperrt, solange ein anderer Lauf offen ist oder kein Phrase-Token konfiguriert ist.',
        },
        'sr-Latn-RS': {
          title: 'Pokretanje sinhronizacije',
          body: 'Otvara kratak dijalog: svi projekti ili samo oni koje izaberete. Run zatim jednom čita ceo Phrase rečnik i pravi pregled projekata. To traje neko vreme — Phrase ograničava koliko brzo odgovara — a stranica se sama osvežava. Zaključano dok je drugi run otvoren ili kad Phrase token nije podešen.',
        },
      },
    },
    {
      selector: '[data-help="phrase-sync-start-scope"]',
      title: 'Choose which projects',
      body: 'Every project, or only these. A run over every project reads the whole Phrase dictionary, which takes a while; picked projects are read by their tags only, which takes minutes. Either way a project nothing changed for in Phrase since the last sync is skipped by the run itself.',
      section: 'Running a sync',
      context: 'start-dialog',
      i18n: {
        'de-AT': {
          title: 'Die Projekte wählen',
          body: 'Alle Projekte oder nur diese. Ein Lauf über alle Projekte liest das gesamte Phrase-Wörterbuch, was eine Weile dauert; ausgewählte Projekte werden nur über ihre Tags gelesen, was Minuten dauert. In beiden Fällen überspringt der Lauf selbst jedes Projekt, für das sich in Phrase seit der letzten Synchronisierung nichts geändert hat.',
        },
        'sr-Latn-RS': {
          title: 'Izbor projekata',
          body: 'Svi projekti ili samo ovi. Run nad svim projektima čita ceo Phrase rečnik, što traje; izabrani projekti se čitaju samo preko svojih tagova, što traje nekoliko minuta. U oba slučaja run sam preskače projekat za koji se u Phrase-u ništa nije promenilo od poslednje sinhronizacije.',
        },
      },
    },
    {
      selector: '[data-help="phrase-sync-start-projects"]',
      title: 'Pick the projects',
      body: 'The projects Glossa imported from Phrase. A project that owns new keys a picked one links joins the run by itself — the link needs its key — and the run says why it was added.',
      section: 'Running a sync',
      context: 'start-dialog',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die Projekte auswählen',
          body: 'Die Projekte, die Glossa aus Phrase importiert hat. Ein Projekt, das neue Schlüssel besitzt, die ein ausgewähltes verlinkt, kommt von selbst in den Lauf — der Link braucht seinen Schlüssel — und der Lauf sagt, warum es dabei ist.',
        },
        'sr-Latn-RS': {
          title: 'Biranje projekata',
          body: 'Projekti koje je Glossa uvezla iz Phrase-a. Projekat koji poseduje nove ključeve koje izabrani linkuje sam ulazi u run — link traži svoj ključ — a run kaže zašto je dodat.',
        },
      },
    },
    {
      selector: '[data-help="phrase-sync-start-confirm"]',
      title: 'Start the run',
      body: 'Queues the run; the scheduler picks it up within seconds. Disabled while “Only these projects” has none picked.',
      section: 'Running a sync',
      context: 'start-dialog',
      i18n: {
        'de-AT': {
          title: 'Den Lauf starten',
          body: 'Stellt den Lauf in die Warteschlange; der Scheduler übernimmt ihn innerhalb von Sekunden. Gesperrt, solange bei „Only these projects“ keines gewählt ist.',
        },
        'sr-Latn-RS': {
          title: 'Pokretanje run-a',
          body: 'Stavlja run u red; scheduler ga preuzima za nekoliko sekundi. Zaključano dok kod „Only these projects“ nijedan nije izabran.',
        },
      },
    },
    {
      selector: '[data-help="phrase-sync-run"]',
      title: 'Follow the current run',
      body: 'The newest run: reading Phrase, previewing, waiting for you, applying, or finished. While it works, the page reads it again every 10 minutes. A preview that is not applied within a day expires, because Phrase and Glossa have both moved on since.',
      section: 'Running a sync',
      i18n: {
        'de-AT': {
          title: 'Den aktuellen Lauf verfolgen',
          body: 'Der neueste Lauf: liest Phrase, erstellt die Vorschau, wartet auf Sie, wird angewendet oder ist fertig. Solange er arbeitet, liest die Seite ihn alle 10 Minuten neu. Eine Vorschau, die nicht innerhalb eines Tages angewendet wird, verfällt, weil sich Phrase und Glossa inzwischen verändert haben.',
        },
        'sr-Latn-RS': {
          title: 'Praćenje trenutnog run-a',
          body: 'Najnoviji run: čita Phrase, pravi pregled, čeka vas, primenjuje se ili je završen. Dok radi, stranica ga ponovo čita na svakih 10 minuta. Pregled koji se ne primeni u roku od jednog dana ističe, jer su se i Phrase i Glossa u međuvremenu promenili.',
        },
      },
    },
    {
      selector: '[data-help="phrase-sync-refresh"]',
      title: 'Refresh now',
      body: 'Reads the run, the history and the conflicts at once, instead of waiting for the next 10-minute look.',
      section: 'Running a sync',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Jetzt aktualisieren',
          body: 'Liest den Lauf, den Verlauf und die Konflikte sofort neu, statt auf die nächste Prüfung nach 10 Minuten zu warten.',
        },
        'sr-Latn-RS': {
          title: 'Osveži odmah',
          body: 'Odmah ponovo čita run, istoriju i konflikte, umesto da se čeka sledeće čitanje za 10 minuta.',
        },
      },
    },
    {
      selector: '[data-help="phrase-sync-summary"]',
      title: 'Read the totals',
      body: 'How many texts the run would write (or wrote), how many new keys it creates, how many Glossa edits it keeps, how many conflicts it found, how many projects stopped, and how many requests it sent to Phrase.',
      section: 'Running a sync',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die Summen lesen',
          body: 'Wie viele Texte der Lauf schreiben würde (oder geschrieben hat), wie viele neue Schlüssel er anlegt, wie viele Glossa-Änderungen er behält, wie viele Konflikte er gefunden hat, wie viele Projekte gestoppt wurden und wie viele Anfragen an Phrase gingen.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje zbirova',
          body: 'Koliko tekstova bi run upisao (ili je upisao), koliko novih ključeva pravi, koliko izmena iz Glosse zadržava, koliko konflikata je našao, koliko projekata je zaustavljeno i koliko zahteva je poslao Phrase-u.',
        },
      },
    },
    {
      selector: '[data-help="phrase-sync-projects"]',
      title: 'Check each project',
      body: 'One row per project, owners of shared keys first. “Unchanged” means nothing moved in Phrase for it, so it was neither downloaded nor previewed and the apply skips it. “Stopped” says why: a new key prefix nobody mapped yet, a publish target that is not allowed, or an export that does not match Phrase. A stopped project is skipped when you apply; the others are not held up. “Added” marks a project nobody picked that owns new keys a picked one links.',
      section: 'Running a sync',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Jedes Projekt prüfen',
          body: 'Eine Zeile pro Projekt, Eigentümer geteilter Schlüssel zuerst. „Unchanged“ heißt, dass sich in Phrase dafür nichts bewegt hat; es wurde weder heruntergeladen noch in der Vorschau geprüft, und das Anwenden überspringt es. „Stopped“ sagt, warum: ein neues Schlüsselpräfix, das noch niemand zugeordnet hat, ein nicht erlaubtes Publish-Ziel oder ein Export, der nicht mit Phrase übereinstimmt. Ein gestopptes Projekt wird beim Anwenden übersprungen; die anderen warten nicht darauf. „Added“ markiert ein nicht gewähltes Projekt, das neue Schlüssel besitzt, die ein gewähltes verlinkt.',
        },
        'sr-Latn-RS': {
          title: 'Provera svakog projekta',
          body: 'Jedan red po projektu, prvo vlasnici deljenih ključeva. „Unchanged“ znači da se u Phrase-u za njega ništa nije promenilo, pa nije ni preuzet ni pregledan, a primena ga preskače. „Stopped“ kaže zašto: novi prefiks ključa koji još niko nije mapirao, publish target koji nije dozvoljen ili export koji se ne poklapa sa Phrase-om. Zaustavljen projekat se preskače pri primeni; ostali ne čekaju na njega. „Added“ označava projekat koji niko nije izabrao, a poseduje nove ključeve koje izabrani linkuje.',
        },
      },
    },
    {
      selector: '[data-help="phrase-sync-missing"]',
      title: 'See keys Phrase no longer has',
      body: 'Keys Glossa holds that were in Phrase at an earlier sync and are gone now. Only a run over every project checks this, because a run over picked projects reads their tags only. The sync deletes nothing; remove a key in the web app if it is gone for good.',
      section: 'Running a sync',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Schlüssel sehen, die Phrase nicht mehr hat',
          body: 'Schlüssel in Glossa, die bei einer früheren Synchronisierung in Phrase waren und jetzt fehlen. Das prüft nur ein Lauf über alle Projekte, weil ein Lauf über ausgewählte Projekte nur deren Tags liest. Die Synchronisierung löscht nichts; entfernen Sie einen Schlüssel in der Web-App, wenn er endgültig weg ist.',
        },
        'sr-Latn-RS': {
          title: 'Ključevi kojih u Phrase-u više nema',
          body: 'Ključevi u Glossi koji su bili u Phrase-u pri ranijoj sinhronizaciji, a sada ih nema. To proverava samo run nad svim projektima, jer run nad izabranim projektima čita samo njihove tagove. Sinhronizacija ništa ne briše; obrišite ključ u web aplikaciji ako je zaista nestao.',
        },
      },
    },
    {
      selector: '[data-help="phrase-sync-apply"]',
      title: 'Apply the preview',
      body: 'Writes exactly what the preview showed, project by project. Each project is written only if its export still matches Phrase afterwards; otherwise nothing of it is kept and it is marked stopped. Nothing is published.',
      section: 'Running a sync',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die Vorschau anwenden',
          body: 'Schreibt genau das, was die Vorschau gezeigt hat, Projekt für Projekt. Ein Projekt wird nur geschrieben, wenn sein Export danach weiterhin mit Phrase übereinstimmt; sonst bleibt nichts davon und es wird als gestoppt markiert. Es wird nichts veröffentlicht.',
        },
        'sr-Latn-RS': {
          title: 'Primena pregleda',
          body: 'Upisuje tačno ono što je pregled pokazao, projekat po projekat. Projekat se upisuje samo ako se njegov export i posle toga poklapa sa Phrase-om; u suprotnom se ništa od njega ne zadržava i označava se kao zaustavljen. Ništa se ne objavljuje.',
        },
      },
    },
    {
      selector: '[data-help="phrase-sync-cancel"]',
      title: 'Cancel a run',
      body: 'A run waiting to start or waiting for you stops at once. A run that is reading or applying stops before its next project; projects already applied stay applied.',
      section: 'Running a sync',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Einen Lauf abbrechen',
          body: 'Ein Lauf, der auf den Start oder auf Sie wartet, stoppt sofort. Ein Lauf, der gerade liest oder anwendet, stoppt vor seinem nächsten Projekt; bereits angewendete Projekte bleiben angewendet.',
        },
        'sr-Latn-RS': {
          title: 'Otkazivanje run-a',
          body: 'Run koji čeka početak ili čeka vas zaustavlja se odmah. Run koji čita ili primenjuje zaustavlja se pre sledećeg projekta; već primenjeni projekti ostaju primenjeni.',
        },
      },
    },
    // ── Conflicts ───────────────────────────────────────────────────────────────
    {
      selector: '[data-help="phrase-sync-conflicts"]',
      title: 'Review the conflicts',
      body: 'Texts that changed in Phrase and in Glossa since the last sync. Each row shows the text at the last sync, Phrase’s text now and Glossa’s text now. Until you decide, Glossa’s text stays and the export uses it.',
      section: 'Conflicts',
      i18n: {
        'de-AT': {
          title: 'Die Konflikte prüfen',
          body: 'Texte, die sich seit der letzten Synchronisierung in Phrase und in Glossa geändert haben. Jede Zeile zeigt den Text bei der letzten Synchronisierung, den aktuellen Text in Phrase und den aktuellen Text in Glossa. Bis Sie entscheiden, bleibt der Glossa-Text und der Export verwendet ihn.',
        },
        'sr-Latn-RS': {
          title: 'Pregled konflikata',
          body: 'Tekstovi koji su se od poslednje sinhronizacije promenili i u Phrase-u i u Glossi. Svaki red prikazuje tekst pri poslednjoj sinhronizaciji, sadašnji tekst u Phrase-u i sadašnji tekst u Glossi. Dok ne odlučite, ostaje tekst iz Glosse i export koristi njega.',
        },
      },
    },
    {
      selector: '[data-help="phrase-sync-conflict-phrase"]',
      title: 'Compare Phrase’s text',
      body: 'What translators wrote in Phrase since the last sync.',
      section: 'Conflicts',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Den Phrase-Text vergleichen',
          body: 'Was Übersetzerinnen und Übersetzer seit der letzten Synchronisierung in Phrase geschrieben haben.',
        },
        'sr-Latn-RS': {
          title: 'Poređenje teksta iz Phrase-a',
          body: 'Šta su prevodioci napisali u Phrase-u od poslednje sinhronizacije.',
        },
      },
    },
    {
      selector: '[data-help="phrase-sync-conflict-glossa"]',
      title: 'Compare Glossa’s text',
      body: 'What somebody saved in Glossa since the last sync — the text the export uses now.',
      section: 'Conflicts',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Den Glossa-Text vergleichen',
          body: 'Was seit der letzten Synchronisierung in Glossa gespeichert wurde — der Text, den der Export jetzt verwendet.',
        },
        'sr-Latn-RS': {
          title: 'Poređenje teksta iz Glosse',
          body: 'Šta je neko sačuvao u Glossi od poslednje sinhronizacije — tekst koji export sada koristi.',
        },
      },
    },
    {
      selector: '[data-help="phrase-sync-take-phrase"]',
      title: 'Take Phrase’s text',
      body: 'Writes Phrase’s text into Glossa and closes the conflict. Refused if the Glossa text changed again after the sync found the conflict — run the sync again then.',
      section: 'Conflicts',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Den Phrase-Text übernehmen',
          body: 'Schreibt den Phrase-Text in Glossa und schließt den Konflikt. Abgelehnt, wenn sich der Glossa-Text geändert hat, nachdem die Synchronisierung den Konflikt gefunden hat — starten Sie dann die Synchronisierung erneut.',
        },
        'sr-Latn-RS': {
          title: 'Preuzimanje teksta iz Phrase-a',
          body: 'Upisuje tekst iz Phrase-a u Glossu i zatvara konflikt. Odbija se ako se tekst u Glossi ponovo promenio pošto je sinhronizacija našla konflikt — tada ponovo pokrenite sinhronizaciju.',
        },
      },
    },
    {
      selector: '[data-help="phrase-sync-keep-glossa"]',
      title: 'Keep Glossa’s text',
      body: 'Closes the conflict and leaves the cell as it is. Later syncs will not raise it again unless Phrase changes that text once more.',
      section: 'Conflicts',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Den Glossa-Text behalten',
          body: 'Schließt den Konflikt und lässt die Zelle, wie sie ist. Spätere Synchronisierungen melden ihn nicht mehr, außer Phrase ändert diesen Text erneut.',
        },
        'sr-Latn-RS': {
          title: 'Zadržavanje teksta iz Glosse',
          body: 'Zatvara konflikt i ostavlja ćeliju kakva jeste. Kasnije sinhronizacije ga više ne prijavljuju, osim ako Phrase ponovo promeni taj tekst.',
        },
      },
    },
    // ── History ─────────────────────────────────────────────────────────────────
    {
      selector: '[data-help="phrase-sync-history"]',
      title: 'Look up earlier runs',
      body: 'The last runs with when they started, how they ended and when they finished.',
      section: 'History',
      i18n: {
        'de-AT': {
          title: 'Frühere Läufe nachsehen',
          body: 'Die letzten Läufe mit Start, Ergebnis und Ende.',
        },
        'sr-Latn-RS': {
          title: 'Raniji run-ovi',
          body: 'Poslednji run-ovi sa vremenom početka, ishodom i vremenom završetka.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Phrase-Synchronisierung',
      intro:
        'Bis alle Anwendungen auf Glossa umgestellt sind, arbeiten Übersetzerinnen und Übersetzer weiter in Phrase. Eine Synchronisierung liest Phrase einmal, zeigt pro Projekt, was sich ändern würde, und schreibt nichts, bis Sie diese Vorschau anwenden. Nur ein Superadmin sieht diese Seite.',
      sections: {
        'Running a sync': 'Eine Synchronisierung ausführen',
        Conflicts: 'Konflikte',
        History: 'Verlauf',
      },
      contexts: {
        'start-dialog': 'Klicken Sie rechts oben auf „Start sync“.',
      },
      tips: [
        'Ein nur in Phrase geänderter Text wird in Glossa geschrieben. Ein nur in Glossa geänderter Text bleibt. Ein in beiden geänderter Text wird zum Konflikt, und der Glossa-Text bleibt, bis Sie entscheiden.',
        'Glossa vergleicht jeden Text mit dem, was Phrase bei der letzten Synchronisierung hatte, nicht damit, wer ihn zuletzt gespeichert hat — daher werden Texte, die vor der ersten Synchronisierung nach Glossa kopiert wurden, als Phrase-Texte erkannt.',
        'Eine Synchronisierung löscht nie einen Schlüssel und leert nie einen Text: Was aus Phrase verschwunden ist, wird nur gemeldet.',
        'Es wird nichts veröffentlicht. Ein Projekt wird gestoppt statt synchronisiert, wenn seine Änderung ein Publish-Ziel erreichen würde, das nicht auf der erlaubten Liste dieser Installation steht, oder irgendein Ziel mit Auto-Publish.',
      ],
    },
    'sr-Latn-RS': {
      title: 'Phrase sinhronizacija',
      intro:
        'Dok sve aplikacije ne pređu na Glossu, prevodioci nastavljaju da rade u Phrase-u. Sinhronizacija jednom čita Phrase, pokazuje po projektu šta bi se promenilo i ne upisuje ništa dok ne primenite taj pregled. Ovu stranicu vidi samo superadmin.',
      sections: {
        'Running a sync': 'Pokretanje sinhronizacije',
        Conflicts: 'Konflikti',
        History: 'Istorija',
      },
      contexts: {
        'start-dialog': 'Kliknite na „Start sync“ u gornjem desnom uglu.',
      },
      tips: [
        'Tekst promenjen samo u Phrase-u upisuje se u Glossu. Tekst promenjen samo u Glossi ostaje. Tekst promenjen na obe strane postaje konflikt, a tekst iz Glosse ostaje dok ne odlučite.',
        'Glossa svaki tekst poredi sa onim što je Phrase imao pri poslednjoj sinhronizaciji, a ne sa tim ko ga je poslednji sačuvao — zato se tekstovi kopirani u Glossu pre prve sinhronizacije prepoznaju kao tekstovi iz Phrase-a.',
        'Sinhronizacija nikad ne briše ključ i nikad ne prazni tekst: ono što je nestalo iz Phrase-a samo se prijavljuje.',
        'Ništa se ne objavljuje. Projekat se zaustavlja umesto da se sinhronizuje ako bi njegova izmena stigla do publish target-a koji nije na listi dozvoljenih za ovu instalaciju, ili do bilo kog target-a sa auto-publish-om.',
      ],
    },
  },
};
